import { HttpClient, HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { ModuleWithProviders, NgModule } from '@angular/core';
import { AngularFireModule } from '@angular/fire';
import { AngularFirestoreModule } from '@angular/fire/firestore';
import { AngularFireStorageModule } from '@angular/fire/storage';

import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { TranslateModule } from '@ngx-translate/core';

import { AgAlertModule } from '@agentifai/core-ui/alert';
import { AgModalModule } from '@agentifai/core-ui/modal';

import { environment } from 'src/environments/environment';

import {
    AuthenticationService,
    ChannelsService,
    ClientConnectionsService,
    ConfigurationService,
    CountersService,
    DepartmentsService,
    FireStorageService,
    FirestoreAuthenticationService,
    FirestoreChannelsService,
    FirestoreClientConnectionsService,
    FirestoreConfigurationService,
    FirestoreCountersService,
    FirestoreDepartmentsService,
    FirestoreMediaStorageService,
    FirestoreMotivesService,
    FirestoreOperatorStatusesService,
    FirestoreRolesService,
    FirestoreTagsService,
    FirestoreTeamsService,
    FirestoreTranslationService,
    FirestoreTutorialsService,
    FirestoreUsersManagementService,
    FirestoreUsersService,
    HttpErrorInterceptor,
    HttpRequestHeadersService,
    HttpRequestParamsService,
    HttpRequestRetryPolicyService,
    HttpRequestService,
    HTTP_REQUEST_RETRY_POLICY_SERVICE_TOKEN_RETRIES,
    HTTP_REQUEST_RETRY_POLICY_SERVICE_TOKEN_SCALING,
    HTTP_REQUEST_RETRY_POLICY_SERVICE_TOKEN_STATUS_CODES,
    HTTP_REQUEST_SERVICE_TOKEN,
    MediaStorageService,
    MotivesService,
    MultiProviderVoiceService,
    OperatorStatusesService,
    PROVIDE_HTTP_REQUEST_PARAMS_SERVICE_FACTORY,
    PROVIDE_HTTP_REQUEST_RETRY_POLICY_SERVICE_FACTORY,
    PROVIDE_HTTP_REQUEST_SERVICE_WITH_API_ENDPOINT_FACTORY,
    PROVIDE_WEBSOCKET_CONNECTION_SERVICE_ENDPOINT_FACTORY,
    PROVIDE_WEBSOCKET_OPTIONS_SERVICE_FACTORY,
    RemoteAssetsService,
    RolesService,
    TagsService,
    TeamsService,
    TranslationContentService,
    TutorialsService,
    UsersManagementService,
    UsersService,
    VoiceService,
    WebSocketConnectionService,
    WebSocketOptionsService,
    WEBSOCKET_REQUEST_SERVICE_TOKEN,
    WEBSOCKET_SERVICE_CONNECTION_OPTIONS,
} from './services';

import { translateCustomLoader } from './translation';

import { DefaultHttpResultProvider, HttpResultProvider } from './providers';

import { reducers } from './store';

export class CoreOptions {
    apiEndpoint: string;

    retryPolicy: {
        numberOfRetries: number;
        scaling: number;
        statusCodes: number[];
    };

    socketOptions?: any;
}

@NgModule({
    imports: [
        AgAlertModule.forRoot({
            translationKeys: {
                yes: 'COMMON.YES',
                no: 'COMMON.NO',
                ok: 'COMMON.OK',
            },
        }),
        AgModalModule,
        AngularFireModule.initializeApp(environment.firebaseConfig),
        AngularFirestoreModule,
        AngularFireStorageModule,
        HttpClientModule,
        StoreModule.forRoot(reducers),
        StoreDevtoolsModule.instrument({
            logOnly: environment.production,
        }),
        TranslateModule.forRoot(translateCustomLoader),
    ],
})
export class CoreModule {
    static forRoot(options: CoreOptions): ModuleWithProviders<CoreModule> {
        return {
            ngModule: CoreModule,
            providers: [
                HttpRequestHeadersService,
                {
                    provide: AuthenticationService,
                    useClass: FirestoreAuthenticationService,
                },
                {
                    provide: ChannelsService,
                    useClass: FirestoreChannelsService,
                },
                {
                    provide: ClientConnectionsService,
                    useClass: FirestoreClientConnectionsService,
                },
                {
                    provide: CountersService,
                    useClass: FirestoreCountersService,
                },
                {
                    provide: ConfigurationService,
                    useClass: FirestoreConfigurationService,
                },
                {
                    provide: DepartmentsService,
                    useClass: FirestoreDepartmentsService,
                },
                {
                    provide: MediaStorageService,
                    useClass: FirestoreMediaStorageService,
                },
                {
                    provide: MotivesService,
                    useClass: FirestoreMotivesService,
                },
                {
                    provide: OperatorStatusesService,
                    useClass: FirestoreOperatorStatusesService,
                },
                {
                    provide: RemoteAssetsService,
                    useClass: FireStorageService,
                },
                {
                    provide: RolesService,
                    useClass: FirestoreRolesService,
                },
                {
                    provide: TagsService,
                    useClass: FirestoreTagsService,
                },
                {
                    provide: TeamsService,
                    useClass: FirestoreTeamsService,
                },
                {
                    provide: TranslationContentService,
                    useClass: FirestoreTranslationService,
                },
                {
                    provide: TutorialsService,
                    useClass: FirestoreTutorialsService,
                },
                {
                    provide: UsersService,
                    useClass: FirestoreUsersService,
                },
                {
                    provide: UsersManagementService,
                    useClass: FirestoreUsersManagementService,
                },
                {
                    provide: HttpResultProvider,
                    useClass: DefaultHttpResultProvider,
                },
                {
                    provide: VoiceService,
                    useClass: MultiProviderVoiceService,
                },
                {
                    provide: HTTP_REQUEST_RETRY_POLICY_SERVICE_TOKEN_RETRIES,
                    useValue: options.retryPolicy.numberOfRetries || 0,
                },
                {
                    provide: HTTP_REQUEST_RETRY_POLICY_SERVICE_TOKEN_SCALING,
                    useValue: options.retryPolicy.scaling || 0,
                },
                {
                    provide: HTTP_REQUEST_RETRY_POLICY_SERVICE_TOKEN_STATUS_CODES,
                    useValue: options.retryPolicy.statusCodes || [],
                },
                {
                    provide: HttpRequestRetryPolicyService,
                    useFactory: PROVIDE_HTTP_REQUEST_RETRY_POLICY_SERVICE_FACTORY,
                    deps: [
                        HTTP_REQUEST_RETRY_POLICY_SERVICE_TOKEN_RETRIES,
                        HTTP_REQUEST_RETRY_POLICY_SERVICE_TOKEN_SCALING,
                        HTTP_REQUEST_RETRY_POLICY_SERVICE_TOKEN_STATUS_CODES,
                    ],
                },
                {
                    provide: HttpRequestParamsService,
                    useFactory: PROVIDE_HTTP_REQUEST_PARAMS_SERVICE_FACTORY,
                },
                {
                    provide: HTTP_REQUEST_SERVICE_TOKEN,
                    useValue: options.apiEndpoint,
                },
                {
                    provide: HttpRequestService,
                    useFactory: PROVIDE_HTTP_REQUEST_SERVICE_WITH_API_ENDPOINT_FACTORY,
                    deps: [
                        HttpClient,
                        HttpRequestHeadersService,
                        HttpRequestParamsService,
                        HttpRequestRetryPolicyService,
                        HTTP_REQUEST_SERVICE_TOKEN,
                    ],
                },
                {
                    provide: HTTP_INTERCEPTORS,
                    useClass: HttpErrorInterceptor,
                    multi: true,
                    deps: [HttpResultProvider],
                },
                {
                    provide: WEBSOCKET_REQUEST_SERVICE_TOKEN,
                    useValue: options.apiEndpoint,
                },
                {
                    provide: WEBSOCKET_SERVICE_CONNECTION_OPTIONS,
                    useValue: options.socketOptions || {},
                },
                {
                    provide: WebSocketOptionsService,
                    useFactory: PROVIDE_WEBSOCKET_OPTIONS_SERVICE_FACTORY,
                    deps: [AuthenticationService, WEBSOCKET_SERVICE_CONNECTION_OPTIONS],
                },
                {
                    provide: WebSocketConnectionService,
                    useFactory: PROVIDE_WEBSOCKET_CONNECTION_SERVICE_ENDPOINT_FACTORY,
                    deps: [WebSocketOptionsService, WEBSOCKET_REQUEST_SERVICE_TOKEN],
                },
            ],
        };
    }
}
